import { Camera } from "lucide-react";
import { Link } from "react-router-dom";

// Real photos from past KNV Travelz group trips that aren't currently
// featured on any specific tour detail page. This is the home for them.
const PAST_TRIPS = [
  { src: "/brand/tour-02.jpg", caption: "Group at the Jefferson Memorial", location: "Washington, DC" },
  { src: "/brand/tour-22.jpg", caption: "Beverly Hills welcome sign", location: "Los Angeles, CA" },
  { src: "/brand/tour-29.jpg", caption: "Vanderbilt University campus visit", location: "Nashville, TN" },
  { src: "/brand/tour-28.jpg", caption: "Vandy day with the group", location: "Nashville, TN" },
  { src: "/brand/tour-09.jpg", caption: "Youth program field trip at the art museum", location: "Milwaukee, WI" },
  { src: "/brand/tour-07.jpg", caption: "Manhattan skyline at sunset", location: "New York, NY" },
  { src: "/brand/tour-01.jpg", caption: "Holiday market strolls", location: "New York, NY" },
  { src: "/brand/tour-10.jpg", caption: "Theme park day with the squad", location: "On the road" },
  { src: "/brand/tour-05.jpg", caption: "Game night on the cruise", location: "Caribbean cruise" },
  { src: "/brand/tour-14.jpg", caption: "Resort sunshine", location: "Caribbean" },
  { src: "/brand/tour-09.jpg", caption: "Youth program field trip at the art museum", location: "Milwaukee, WI" },
  { src: "/brand/tour-17.jpg", caption: "Beach sign stops", location: "Jamaica" },
  { src: "/brand/tour-22.jpg", caption: "Beverly Hills welcome sign", location: "Los Angeles, CA" },
  { src: "/brand/tour-19.jpg", caption: "Yosemite tour day", location: "Yosemite, CA" },
  { src: "/brand/tour-12.jpg", caption: "Yosemite Valley overlook", location: "California" },
  { src: "/brand/tour-13.jpg", caption: "Joshua Tree National Park", location: "California" },
  { src: "/brand/tour-20.jpg", caption: "On Route 66", location: "American Southwest" },
  { src: "/brand/tour-21.jpg", caption: "Bryce Canyon hike", location: "Utah" },
];

export default function PastTrips() {
  return (
    <div className="bg-cream pb-24" data-testid="past-trips-page">
      {/* HERO */}
      <section className="container-edge pt-16 pb-12">
        <div className="max-w-3xl">
          <div className="eyebrow mb-4" data-testid="past-trips-eyebrow">
            <Camera size={14} className="text-coral" /> The Trip Wall
          </div>
          <h1 className="display-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.04] tracking-tight">
            Real groups.{" "}
            <span className="hand-accent text-coral inline-block tilt-l">Real trips.</span>
          </h1>
          <p className="text-muted2 mt-6 leading-relaxed max-w-2xl text-lg">
            A wall of moments from groups we've travelled with — students touring schools, educators on
            spring break, alumni on cruises, families crossing the country. None of these are stock photos.
            They're our people, on the road.
          </p>
        </div>
      </section>

      {/* MASONRY-ISH GRID */}
      <section className="container-edge">
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="past-trips-grid"
        >
          {PAST_TRIPS.map((p, i) => (
            <figure
              key={p.src}
              className={`group relative overflow-hidden rounded-2xl bg-ink/5 ${
                i % 5 === 0 ? "sm:row-span-2 sm:aspect-[3/4]" : "aspect-[4/3]"
              }`}
              data-testid={`past-trip-${i}`}
            >
              <img
                src={p.src}
                alt={p.caption}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              <figcaption className="absolute inset-x-0 bottom-0 p-5 text-cream translate-y-2 group-hover:translate-y-0 opacity-0 group-hover:opacity-100 transition-all duration-300">
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-gold mb-1">
                  {p.location}
                </div>
                <div className="font-display text-lg leading-tight">{p.caption}</div>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="container-edge mt-24">
        <div className="bg-ink text-cream rounded-3xl p-10 sm:p-14 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-80 h-80 bg-coral/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-80 h-80 bg-gold/20 rounded-full blur-3xl" />
          <div className="relative max-w-2xl">
            <h2 className="display-serif text-3xl sm:text-4xl leading-tight">
              Want your group on this wall?
            </h2>
            <p className="mt-4 text-cream/80 leading-relaxed">
              Tell us where you'd like to go. We'll handle the rest — and if you send us your trip pics
              afterward, we'll feature them here.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/request-quote" className="btn-primary" data-testid="past-trips-cta-quote">
                Plan My Trip
              </Link>
              <Link
                to="/tours"
                className="px-6 py-3 rounded-full border border-cream/30 text-cream hover:bg-cream/10 transition-colors text-sm font-medium"
                data-testid="past-trips-cta-tours"
              >
                Browse Tours
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
